import {
  Description,
  FiberNew,
  FilterAltOff,
  LoginOutlined,
  PersonAdd,
  PhoneAndroid,
  Public,
  Search,
  ShoppingCartCheckout,
  SyncDisabled,
  ThumbDownAlt
} from "@mui/icons-material";
import {
  Box,
  Button,
  Fade,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { Bar, Doughnut, Pie } from "react-chartjs-2";
import styled, { keyframes } from "styled-components";
import { useToastStore } from "../../../stores";
import { colorPrimary } from "../../../theme";
import { formatCurrencyBRL } from "../../../utils/mask/MaskUtils";
import { getFirstDayOfMonth } from "../../../utils/methods/inititalDateTimeFilter";
import { exportShopkeeperAnaliticooCsv, fetchShopkeeperAnalitico } from "../repository/AdminRepository";
import { ShopkeeperAnaliticoData } from "../types/types";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Filler, Tooltip, Legend);

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const AnimatedBox = styled.div<{ $delay?: number }>`
  animation: ${slideUp} 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: ${({ $delay }) => $delay ?? 0}ms;
  opacity: 0;
`;

const CHART_PALETTE = [
  colorPrimary, "#10b981", "#0284c7", "#8b5cf6", "#ef4444",
  "#f59e0b", "#ec4899", "#14b8a6", "#6366f1", "#78716c",
  "#d946ef", "#06b6d4", "#84cc16", "#f97316", "#a855f7",
  "#22d3ee", "#fb923c", "#a3e635", "#e879f9", "#34d399",
];

function formatCurrency(val: number): string {
  return val.toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 0 });
}

function formatNumber(val: number | null): string {
  if (val == null) return "0";
  return val.toLocaleString("pt-BR");
}

const inputSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "white",
    borderRadius: 2,
    fontSize: "0.85rem",
    "& fieldset": { border: "1px solid #e2e8f0" },
    "&:hover fieldset": { borderColor: colorPrimary + "80" },
    "&.Mui-focused fieldset": { borderColor: colorPrimary },
  },
} as const;

const labelSx = {
  component: "label" as const,
  fontSize: "0.7rem",
  fontWeight: 700,
  textTransform: "uppercase" as const,
  letterSpacing: 1,
  color: "#64748b",
  mb: 0.5,
  display: "block",
};

interface KpiCardProps {
  label: string;
  value: string;
  subtitle: string;
  icon: React.ReactNode;
  accentColor: string;
  bgColor: string;
  delay: number;
}

function KpiCard({ label, value, subtitle, icon, accentColor, bgColor, delay }: KpiCardProps) {
  return (
    <AnimatedBox $delay={delay}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          position: "relative",
          overflow: "hidden",
          border: "1px solid #f1f5f9",
          height: "100%",
          transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 12px 40px rgba(0,0,0,0.08)",
            borderColor: accentColor + "40",
          },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography
            fontSize="0.7rem"
            fontWeight={700}
            textTransform="uppercase"
            letterSpacing={1.5}
            color="#94a3b8"
          >
            {label}
          </Typography>
          <Box
            sx={{
              bgcolor: bgColor,
              color: accentColor,
              p: 1,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </Box>
        </Box>
        <Box display="flex" alignItems="baseline" gap={1}>
          <Typography
            fontSize="2rem"
            fontWeight={900}
            letterSpacing={-1.5}
            color={accentColor}
            lineHeight={1}
          >
            {value}
          </Typography>
          <Typography fontSize="0.7rem" fontWeight={600} color={accentColor + "99"}>
            {subtitle}
          </Typography>
        </Box>
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: 3,
            bgcolor: accentColor + "30",
          }}
        />
      </Paper>
    </AnimatedBox>
  );
}

function KpiCardSkeleton({ delay }: { delay: number }) {
  return (
    <AnimatedBox $delay={delay}>
      <Paper
        elevation={0}
        sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9", overflow: "hidden", position: "relative" }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Skeleton variant="text" width={100} height={16} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: 2 }} />
        </Box>
        <Box display="flex" alignItems="baseline" gap={1}>
          <Skeleton variant="text" width={80} height={40} sx={{ borderRadius: 1 }} />
          <Skeleton variant="text" width={60} height={14} sx={{ borderRadius: 1 }} />
        </Box>
        <Box sx={{ position: "absolute", bottom: 0, left: 0, width: "100%", height: 3, bgcolor: "#e2e8f0" }} />
      </Paper>
    </AnimatedBox>
  );
}

function ChartSkeleton() {
  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9" }}>
      <Skeleton variant="text" width={200} height={24} sx={{ mb: 2, borderRadius: 1 }} />
      <Skeleton variant="rounded" width="100%" height={280} sx={{ borderRadius: 2 }} />
    </Paper>
  );
}

function TableSkeleton() {
  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9" }}>
      <Skeleton variant="text" width={180} height={24} sx={{ mb: 2, borderRadius: 1 }} />
      {Array.from({ length: 5 }).map((_, i) => (
        <Box key={i} display="flex" alignItems="center" gap={2} py={1.5}>
          <Skeleton variant="text" width="60%" height={18} sx={{ borderRadius: 1 }} />
          <Skeleton variant="text" width={60} height={18} sx={{ borderRadius: 1, ml: "auto" }} />
        </Box>
      ))}
    </Paper>
  );
}

interface DoughnutLegendItem {
  label: string;
  value: number;
  color: string;
}

function DoughnutLegend({ items }: { items: DoughnutLegendItem[] }) {
  const total = items.reduce((s, i) => s + i.value, 0);
  return (
    <Box display="flex" justifyContent="center" gap={3} mt={2} flexWrap="wrap">
      {items.map((item) => {
        const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : "0.0";
        return (
          <Box key={item.label} display="flex" alignItems="center" gap={1}>
            <Box width={10} height={10} borderRadius="50%" bgcolor={item.color} flexShrink={0} />
            <Typography fontSize="0.8rem" fontWeight={600} color="#475569">
              {item.label}:
            </Typography>
            <Typography fontSize="0.8rem" fontWeight={800} color="#1e293b">
              {formatNumber(item.value)}
            </Typography>
            <Typography fontSize="0.75rem" fontWeight={600} color="#94a3b8">
              ({pct}%)
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

const pieOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "bottom" as const,
      labels: {
        usePointStyle: true,
        padding: 16,
        font: { size: 11, weight: 600 as const },
        color: "#475569",
      },
    },
    tooltip: {
      backgroundColor: "#1e293b",
      titleFont: { size: 13, weight: 700 as const },
      bodyFont: { size: 12 },
      padding: 12,
      cornerRadius: 8,
      callbacks: {
        label: (ctx: any) => {
          const total = ctx.dataset.data.reduce((a: number, b: number) => a + b, 0);
          const pct = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : "0";
          return ` ${ctx.label}: ${formatNumber(ctx.parsed)} (${pct}%)`;
        },
      },
    },
  },
};

const thSx = {
  fontSize: "0.68rem",
  fontWeight: 700,
  textTransform: "uppercase" as const,
  letterSpacing: 1.5,
  color: "#94a3b8",
  borderBottom: "2px solid #f1f5f9",
  py: 1.5,
  whiteSpace: "nowrap" as const,
};

const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "65%",
  plugins: {
    legend: {
      position: "bottom" as const,
      labels: {
        usePointStyle: true,
        padding: 16,
        font: { size: 11, weight: 600 as const },
        color: "#475569",
      },
    },
    tooltip: {
      backgroundColor: "#1e293b",
      titleFont: { size: 13, weight: 700 as const },
      bodyFont: { size: 12 },
      padding: 12,
      cornerRadius: 8,
      callbacks: {
        label: (ctx: any) => {
          const total = ctx.dataset.data.reduce((a: number, b: number) => a + b, 0);
          const pct = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : "0";
          return ` ${ctx.label}: ${ctx.parsed} (${pct}%)`;
        },
      },
    },
  },
};

const TOP_CIDADES_LIMIT = 30;

export function ShopkeeperAnaliticoTab() {
  const [dataInicio, setDataInicio] = useState(getFirstDayOfMonth());
  const [dataFim, setDataFim] = useState(new Date().toISOString().split("T")[0] || "");
  const [dashboard, setDashboard] = useState<ShopkeeperAnaliticoData | null>(null);
  const [loading, setLoading] = useState(true);
  const { setToast } = useToastStore();
  const jaCarregouRef = useRef(false);

  const fetchDash = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fetchShopkeeperAnalitico(dataInicio, dataFim);
      if (!result?.success) {
        setToast({ variant: "error", message: result.message });
        return;
      }
      setDashboard(result.data);
    } catch {
      setToast({ variant: "error", message: "Erro. Contate o administrador." });
    } finally {
      setLoading(false);
    }
  }, [dataInicio, dataFim, setToast]);

  const handleExportCsv = useCallback(async () => {
    try {
      setLoading(true);
      const result = await exportShopkeeperAnaliticooCsv(dataInicio, dataFim);
      if (!result?.success) {
        setToast({ variant: "error", message: result.message ?? "Erro ao exportar CSV." });
      }
    } catch {
      setToast({ variant: "error", message: "Erro ao exportar CSV. Contate o administrador." });
    } finally {
      setLoading(false);
    }
  }, [dataInicio, dataFim, setToast]);

  const resetFilters = useCallback(async () => {
    setDataInicio(getFirstDayOfMonth());
    setDataFim(new Date().toISOString().split("T")[0]);
    await fetchDash();
  }, []);

  useEffect(() => {
    if (jaCarregouRef.current) return;
    jaCarregouRef.current = true;
    fetchDash();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const topLojistas = dashboard?.top_lojistas_compradores ?? [];
  const topCidades = dashboard?.top_cidades_compradoras ?? [];
  const topUfs = dashboard?.top_uf_compradores ?? [];
  const lojistaPorCidade = (dashboard?.lojistas_por_cidade ?? []).filter((c) => c.cidade_label);
  const lojistaPorUf = dashboard?.lojistas_por_uf ?? [];

  const topCidadesLimitadas = lojistaPorCidade.slice(0, TOP_CIDADES_LIMIT);

  // --- Top Lojistas chart ---
  const topLojistasChartData = {
    labels: topLojistas.map((l) => l.revenda_label),
    datasets: [
      {
        label: "Valor Pago (R$)",
        data: topLojistas.map((l) => l.valor_pago),
        backgroundColor: colorPrimary + "cc",
        hoverBackgroundColor: colorPrimary,
        borderRadius: 6,
        maxBarThickness: 32,
        yAxisID: "y",
      },
      {
        label: "Qtd Comprados",
        data: topLojistas.map((l) => l.qtd_anuncio_comprado),
        backgroundColor: "#0284c7cc",
        hoverBackgroundColor: "#0284c7",
        borderRadius: 6,
        maxBarThickness: 32,
        yAxisID: "y1",
      },
    ],
  };

  const topLojistasChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y" as const,
    plugins: {
      legend: {
        position: "top" as const,
        labels: { usePointStyle: true, padding: 20, font: { size: 12, weight: 600 as const } },
      },
      tooltip: {
        backgroundColor: "#1e293b",
        titleFont: { size: 13, weight: 700 as const },
        bodyFont: { size: 12 },
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: (ctx: any) => {
            if (ctx.datasetIndex === 0) return ` ${ctx.dataset.label}: ${formatCurrencyBRL(ctx.parsed.x)}`;
            return ` ${ctx.dataset.label}: ${ctx.parsed.x}`;
          },
        },
      },
    },
    scales: {
      y: {
        grid: { display: false },
        ticks: { font: { size: 11, weight: 600 as const }, color: "#475569" },
      },
      x: {
        position: "top" as const,
        grid: { color: "#f1f5f9" },
        ticks: {
          font: { size: 10 },
          color: "#94a3b8",
          callback: (val: any) => formatCurrency(Number(val) * 100),
        },
      },
      y1: { display: false },
    },
  };

  // --- Doughnut data: Top Cidades Compradoras ---
  const cidadeCompChartData = {
    labels: topCidades.map((c) => c.cidade_label),
    datasets: [{
      data: topCidades.map((c) => c.qtd_per_cidade),
      backgroundColor: CHART_PALETTE.slice(0, topCidades.length),
      hoverBackgroundColor: CHART_PALETTE.slice(0, topCidades.length).map((c) => c + "cc"),
      borderWidth: 0,
    }],
  };

  // --- Doughnut data: Top UF Compradores ---
  const ufCompChartData = {
    labels: topUfs.map((u) => u.uf_label),
    datasets: [{
      data: topUfs.map((u) => u.qtd_per_uf),
      backgroundColor: CHART_PALETTE.slice(0, topUfs.length),
      hoverBackgroundColor: CHART_PALETTE.slice(0, topUfs.length).map((c) => c + "cc"),
      borderWidth: 0,
    }],
  };

  const topLojistasChartHeight = Math.max(320, topLojistas.length * 48);

  return (
    <Fade in timeout={400}>
      <Box display="flex" flexDirection="column" gap={3}>
        {/* FILTROS */}
        <AnimatedBox $delay={0}>
          <Paper elevation={0} sx={{ p: 3.5, borderRadius: 3, border: "1px solid #f1f5f9" }}>
            <Typography
              fontSize="0.7rem"
              fontWeight={700}
              textTransform="uppercase"
              letterSpacing={1.5}
              color="#94a3b8"
              mb={2.5}
            >
              Filtros Analítico Lojistas
            </Typography>
            <Box
              display="grid"
              gridTemplateColumns={{ xs: "1fr", sm: "1fr 0.4fr" }}
              gap={2}
              alignItems="end"
            >
              <Box>
                <Typography {...labelSx}>Período</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                    sx={inputSx}
                  />
                  <Typography fontSize="0.75rem" color="#94a3b8" flexShrink={0}>
                    até
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    value={dataFim}
                    onChange={(e) => setDataFim(e.target.value)}
                    sx={inputSx}
                  />
                </Box>
              </Box>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  startIcon={<Search sx={{ fontSize: "17px !important" }} />}
                  disableElevation
                  disabled={loading}
                  onClick={fetchDash}
                  sx={{
                    color: colorPrimary,
                    borderColor: "#e2e8f0",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    textTransform: "none",
                    borderRadius: 2,
                    px: 3,
                    height: 40,
                    whiteSpace: "nowrap",
                    bgcolor: "white",
                    "&:hover": {
                      borderColor: colorPrimary,
                      bgcolor: colorPrimary + "08",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  Gerar
                </Button>
                <Button
              variant="text"
              startIcon={<FilterAltOff sx={{ fontSize: "17px !important" }} />}
              disabled={loading}
              onClick={resetFilters}
              sx={{
                color: "#94a3b8",
                fontWeight: 600,
                fontSize: "0.85rem",
                textTransform: "none",
                borderRadius: 2,
                px: 2,
                height: 40,
                whiteSpace: "nowrap",
                "&:hover": {
                  color: colorPrimary,
                  bgcolor: colorPrimary + "08",
                },
                transition: "all 0.2s",
              }}
            >
              Limpar
            </Button>
              </Box>
            </Box>
          </Paper>
        </AnimatedBox>

        {/* STATUS DAS REVENDAS (Pie) + KPI Novas */}
        <Box display="grid" gridTemplateColumns={{ xs: "1fr", lg: "2fr 1fr" }} gap={2.5}>
          <AnimatedBox $delay={80}>
            {loading ? (
              <ChartSkeleton />
            ) : (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9", height: "100%" }}>
                 <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                  <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                    Status das Revendas
                  </Typography>
                  <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                    Total de revendas cadastradas: {formatNumber(dashboard?.total_qtd_revenda ?? null)}
                  </Typography>
                  </Box>
                  <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Description sx={{ color: "#10b981" }} />}
                  onClick={handleExportCsv}
                  disabled={loading}
                  sx={{
                    borderColor: "#e2e8f0",
                    color: "#475569",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    borderRadius: 2,
                    textTransform: "none",
                    height: 40,
                    "&:hover": {
                      borderColor: "#10b981",
                      bgcolor: "#ecfdf5",
                    },
                  }}
                >
                  CSV
                </Button>
                 </Stack>
                <Box height={280} display="flex" alignItems="center" justifyContent="center">
                  <Pie
                    data={{
                      labels: ["Ativas", "Inativas", "Recusadas"],
                      datasets: [{
                        data: [
                          dashboard?.total_qtd_revenda_ativa ?? 0,
                          dashboard?.total_qtd_revenda_inativa ?? 0,
                          dashboard?.total_qtd_revenda_recusada ?? 0,
                        ],
                        backgroundColor: ["#10b981", "#ef4444", "#f59e0b"],
                        hoverBackgroundColor: ["#10b981cc", "#ef4444cc", "#f59e0bcc"],
                        borderWidth: 0,
                      }],
                    }}
                    options={pieOptions}
                  />
                </Box>
                <DoughnutLegend items={[
                  { label: "Ativas", value: dashboard?.total_qtd_revenda_ativa ?? 0, color: "#10b981" },
                  { label: "Inativas", value: dashboard?.total_qtd_revenda_inativa ?? 0, color: "#ef4444" },
                  { label: "Recusadas", value: dashboard?.total_qtd_revenda_recusada ?? 0, color: "#f59e0b" },
                ]} />
              </Paper>
            )}
          </AnimatedBox>

          <Box display="flex" flexDirection="column" gap={2.5}>
            <AnimatedBox $delay={200}>
              {loading ? (
                <KpiCardSkeleton delay={200} />
              ) : (
                <KpiCard
                  label="Novas no Período"
                  value={formatNumber(dashboard?.qtd_nova_revenda ?? null)}
                  subtitle="revendas"
                  icon={<FiberNew />}
                  accentColor="#8b5cf6"
                  bgColor="#f5f3ff"
                  delay={200}
                />
              )}
            </AnimatedBox>
            <AnimatedBox $delay={260}>
              {loading ? (
                <KpiCardSkeleton delay={260} />
              ) : (
                <KpiCard
                  label="Novos Lojistas Compradores"
                  value={formatNumber(dashboard?.qtd_nova_revenda_compradora ?? null)}
                  subtitle="lojistas"
                  icon={<PersonAdd />}
                  accentColor="#0284c7"
                  bgColor="#e0f2fe"
                  delay={260}
                />
              )}
            </AnimatedBox>
          </Box>
        </Box>

        {/* COMPRAS NO PERIODO (Pie) + KPIs */}
        <Box display="grid" gridTemplateColumns={{ xs: "1fr", lg: "2fr 1fr" }} gap={2.5}>
          <AnimatedBox $delay={380}>
            {loading ? (
              <ChartSkeleton />
            ) : (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9", height: "100%" }}>
                <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                  Compras no Período
                </Typography>
                <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                  Revendas com e sem compras no período selecionado
                </Typography>
                <Box height={280} display="flex" alignItems="center" justifyContent="center">
                  <Pie
                    data={{
                      labels: ["Sem Compras", "Compradora"],
                      datasets: [{
                        data: [
                          dashboard?.qtd_revenda_sem_compra_periodo ?? 0,
                          dashboard?.qtd_revenda_compradora_periodo ?? 0,
                        ],
                        backgroundColor: ["#ef4444", "#10b981"],
                        hoverBackgroundColor: ["#ef4444cc", "#10b981cc"],
                        borderWidth: 0,
                      }],
                    }}
                    options={pieOptions}
                  />
                </Box>
                <DoughnutLegend items={[
                  { label: "Sem Compras", value: dashboard?.qtd_revenda_sem_compra_periodo ?? 0, color: "#ef4444" },
                  { label: "Compradora", value: dashboard?.qtd_revenda_compradora_periodo ?? 0, color: "#10b981" },
                ]} />
              </Paper>
            )}
          </AnimatedBox>

          <Box display="flex" flexDirection="column" gap={2.5}>
            {loading ? (
              <>
                <KpiCardSkeleton delay={420} />
                <KpiCardSkeleton delay={460} />
                <KpiCardSkeleton delay={500} />
              </>
            ) : (
              <>
                <KpiCard
                  label="Sem Nenhuma Compra"
                  value={formatNumber(dashboard?.qtd_revenda_sem_compra_nenhuma ?? null)}
                  subtitle="revendas"
                  icon={<ShoppingCartCheckout />}
                  accentColor="#ef4444"
                  bgColor="#fef2f2"
                  delay={420}
                />
                <KpiCard
                  label="Sem Recompra"
                  value={formatNumber(dashboard?.qtd_revenda_sem_recompra ?? null)}
                  subtitle="revendas"
                  icon={<SyncDisabled />}
                  accentColor="#ec4899"
                  bgColor="#fdf2f8"
                  delay={460}
                />
                <KpiCard
                  label="Sem Logar 30 dias"
                  value={formatNumber(dashboard?.qtd_sem_logar_30_dias ?? null)}
                  subtitle="revendas"
                  icon={<LoginOutlined />}
                  accentColor="#64748b"
                  bgColor="#f1f5f9"
                  delay={500}
                />
              </>
            )}
          </Box>
        </Box>

        {/* KPI ROW 3 */}
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr 1fr" }}
          gap={2.5}
        >
          {loading ? (
            <>
              <KpiCardSkeleton delay={540} />
              <KpiCardSkeleton delay={580} />
              <KpiCardSkeleton delay={620} />
            </>
          ) : (
            <>
              <KpiCard
                label="Sem Proposta"
                value={formatNumber(dashboard?.qtd_revenda_sem_proposta ?? null)}
                subtitle="revendas"
                icon={<ThumbDownAlt />}
                accentColor="#0284c7"
                bgColor="#e0f2fe"
                delay={540}
              />
              <KpiCard
                label="Sem Proposta Mobile"
                value={formatNumber(dashboard?.qtd_revenda_sem_proposta_mobile ?? null)}
                subtitle="revendas"
                icon={<PhoneAndroid />}
                accentColor="#6366f1"
                bgColor="#eef2ff"
                delay={580}
              />
              <KpiCard
                label="Sem Proposta Web"
                value={formatNumber(dashboard?.qtd_revenda_sem_proposta_web ?? null)}
                subtitle="revendas"
                icon={<Public />}
                accentColor="#14b8a6"
                bgColor="#f0fdfa"
                delay={620}
              />
            </>
          )}
        </Box>

        {/* TOP LOJISTAS COMPRADORES */}
        <AnimatedBox $delay={660}>
          {loading ? (
            <ChartSkeleton />
          ) : (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9" }}>
              <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                Top Lojistas Compradores
              </Typography>
              <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                Valor total pago e quantidade de anúncios comprados
              </Typography>
              <Box height={topLojistasChartHeight}>
                <Bar data={topLojistasChartData} options={topLojistasChartOptions} />
              </Box>
            </Paper>
          )}
        </AnimatedBox>

        {/* TOP CIDADES COMPRADORAS + TOP UF COMPRADORES */}
        <Box display="grid" gridTemplateColumns={{ xs: "1fr", lg: "1fr 1fr" }} gap={3}>
          <AnimatedBox $delay={740}>
            {loading ? (
              <ChartSkeleton />
            ) : (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9", height: "100%" }}>
                <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                  Top Cidades Compradores
                </Typography>
                <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                  Distribuição de compras por cidade
                </Typography>
                <Box height={320} display="flex" alignItems="center" justifyContent="center">
                  <Doughnut data={cidadeCompChartData} options={doughnutOptions} />
                </Box>
              </Paper>
            )}
          </AnimatedBox>

          <AnimatedBox $delay={820}>
            {loading ? (
              <ChartSkeleton />
            ) : (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9", height: "100%" }}>
                <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                  Top UF Compradores
                </Typography>
                <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                  Distribuição de compras por estado
                </Typography>
                <Box height={320} display="flex" alignItems="center" justifyContent="center">
                  <Doughnut data={ufCompChartData} options={doughnutOptions} />
                </Box>
              </Paper>
            )}
          </AnimatedBox>
        </Box>

        {/* LOJISTAS POR CIDADE */}
        <AnimatedBox $delay={900}>
          {loading ? (
            <TableSkeleton />
          ) : (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9" }}>
              <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                Lojistas por Cidade
              </Typography>
              <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                Top {TOP_CIDADES_LIMIT} cidades com mais lojistas cadastrados
              </Typography>
              <TableContainer sx={{ maxHeight: 520 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ "& th": { bgcolor: "#f8fafc" } }}>
                      <TableCell sx={thSx}>Cidade</TableCell>
                      <TableCell sx={{ ...thSx, textAlign: "center" }}>Qtd Lojistas</TableCell>
                      <TableCell sx={{ ...thSx, textAlign: "center" }}>Qtd Ativos</TableCell>
                      <TableCell sx={{ ...thSx, minWidth: 200 }}>Proporção Ativos</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topCidadesLimitadas.map((row) => (
                      <TableRow key={row.cidade_label} sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                        <TableCell
                          sx={{
                            py: 2,
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            color: "#1e293b",
                            borderBottom: "1px solid #f8fafc",
                          }}
                        >
                          {row.cidade_label}
                        </TableCell>
                        <TableCell
                          sx={{
                            py: 2,
                            fontSize: "0.85rem",
                            fontWeight: 800,
                            color: colorPrimary,
                            textAlign: "center",
                            borderBottom: "1px solid #f8fafc",
                          }}
                        >
                          {formatNumber(row.qtd_lojista_per_cidade)}
                        </TableCell>
                        <TableCell
                          sx={{
                            py: 2,
                            fontSize: "0.85rem",
                            fontWeight: 800,
                            color: "#10b981",
                            textAlign: "center",
                            borderBottom: "1px solid #f8fafc",
                          }}
                        >
                          {formatNumber(row.qtd_ativo_per_cidade)}
                        </TableCell>
                        <TableCell sx={{ py: 2, borderBottom: "1px solid #f8fafc" }}>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Box flex={1}>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(row.percentual_ativo_per_cidade ?? 0, 100)}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  bgcolor: "#f1f5f9",
                                  "& .MuiLinearProgress-bar": {
                                    borderRadius: 4,
                                    bgcolor: "#8b5cf6",
                                  },
                                }}
                              />
                            </Box>
                            <Typography fontSize="0.7rem" color="#94a3b8" fontWeight={600} minWidth={40}>
                              {(row.percentual_ativo_per_cidade ?? 0).toFixed(2)}%
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </AnimatedBox>

        {/* LOJISTAS POR UF */}
        <AnimatedBox $delay={1000}>
          {loading ? (
            <TableSkeleton />
          ) : (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9" }}>
              <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                Lojistas por UF
              </Typography>
              <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                Distribuição de lojistas por estado
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f8fafc" }}>
                      <TableCell sx={thSx}>UF</TableCell>
                      <TableCell sx={{ ...thSx, textAlign: "center" }}>Qtd Lojistas</TableCell>
                      <TableCell sx={{ ...thSx, textAlign: "center" }}>Qtd Ativos</TableCell>
                      <TableCell sx={{ ...thSx, minWidth: 200 }}>Proporção Ativos</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lojistaPorUf.map((row) => (
                      <TableRow key={row.uf_label} sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                        <TableCell
                          sx={{
                            py: 2,
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            color: "#1e293b",
                            borderBottom: "1px solid #f8fafc",
                          }}
                        >
                          {row.uf_label}
                        </TableCell>
                        <TableCell
                          sx={{
                            py: 2,
                            fontSize: "0.85rem",
                            fontWeight: 800,
                            color: colorPrimary,
                            textAlign: "center",
                            borderBottom: "1px solid #f8fafc",
                          }}
                        >
                          {formatNumber(row.qtd_lojista_per_uf)}
                        </TableCell>
                        <TableCell
                          sx={{
                            py: 2,
                            fontSize: "0.85rem",
                            fontWeight: 800,
                            color: "#10b981",
                            textAlign: "center",
                            borderBottom: "1px solid #f8fafc",
                          }}
                        >
                          {formatNumber(row.qtd_ativo_per_uf)}
                        </TableCell>
                        <TableCell sx={{ py: 2, borderBottom: "1px solid #f8fafc" }}>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Box flex={1}>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(row.percentual_ativo_per_uf ?? 0, 100)}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  bgcolor: "#f1f5f9",
                                  "& .MuiLinearProgress-bar": {
                                    borderRadius: 4,
                                    bgcolor: "#10b981",
                                  },
                                }}
                              />
                            </Box>
                            <Typography fontSize="0.7rem" color="#94a3b8" fontWeight={600} minWidth={40}>
                              {(row.percentual_ativo_per_uf ?? 0).toFixed(2)}%
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </AnimatedBox>
      </Box>
    </Fade>
  );
}
